import { ExplorerNetwork, RPCNodeNetwork } from './network.js';
import { cChainParams } from '../chain_params.js';
import { debugLog, DebugTopics, debugWarn } from '../debug.js';
import { sleep } from '../utils.js';
import { getEventEmitter } from '../event_bus.js';

class NetworkManager {
    /**
     * @type {import('./network.js').Network} - Current selected Explorer
     */
    #currentExplorer;

    /**
     * @type {import('./network.js').Network} - Current selected RPC node
     */
    #currentNode;

    /**
     * @type {Array<import('./network.js').Network>} - List of all available Networks
     */
    #networks = [];

    start() {
        this.#networks = [];
        for (let network of cChainParams.current.Explorers) {
            this.#networks.push(new ExplorerNetwork(network.url));
        }
        for (let network of cChainParams.current.Nodes) {
            this.#networks.push(new RPCNodeNetwork(network.url));
        }
    }

    /**
     * Reset the list of available networks
     */
    reset() {
        this.#networks = [];
    }

    /**
     * Sets the network in use by MPW.
     * @param {string} strUrl - network to use
     * @param {boolean} isRPC - whether we are setting the explorer or the RPC node
     */
    setNetwork(strUrl, isRPC) {
        if (this.#networks.length === 0) {
            this.start();
        }
        const found = this.#networks.find(
            (network) => network.strUrl === strUrl
        );
        if (!found) throw new Error('Cannot find provided Network!');
        if (isRPC) {
            this.#currentNode = found;
        } else {
            this.#currentExplorer = found;
        }

        getEventEmitter().emit(
            isRPC ? 'rpc_changed' : 'explorer_changed',
            strUrl
        );
    }

    /**
     * Changes RPC and explorer to next one
     * To be called in case of an error believed to be a network one
     * e.g. wrong sapling root
     */
    rotateNetworks() {
        const currentExplorerIndex = this.#networks.findIndex(
            (n) => n === this.#currentExplorer
        );
        const currentNodeIndex = this.#networks.findIndex(
            (n) => n === this.#currentNode
        );
        const indices = [currentExplorerIndex, currentNodeIndex];
        for (let j = 0; j < indices.length; j++) {
            for (let i = 1; i < this.#networks.length; i++) {
                const isRpc = j === 1;
                const index = indices[j];
                const tryNetwork =
                    this.#networks[(index + i) % this.#networks.length];
                if (tryNetwork.isRpc === isRpc) {
                    if (isRpc) {
                        this.#currentNode = tryNetwork;
                    } else {
                        this.#currentExplorer = tryNetwork;
                    }
                    break;
                }
            }
        }
    }

    /**
     * Call all networks until one is succesful
     * seamlessly attempt the same call on multiple other instances until success.
     * @param {string} funcName - The function to re-attempt with
     * @param {boolean} isRPC - Whether to begin with the selected explorer or RPC node
     * @param {number} retryTimeout - How long we should wait before retrying in milliseconds
     * @param  {...any} args - The arguments to pass to the function
     */
    async #retryWrapper(funcName, isRPC, retryTimeout = 0, ...args) {
        const candidateNetworks = this.#networks.filter(
            (network) => network.isRpc === isRPC
        );
        if (candidateNetworks.length === 0) {
            throw new Error(
                `No ${isRPC ? 'RPC' : 'Explorer'} networks configured`
            );
        }

        const preferredNetwork = isRPC
            ? this.#currentNode
            : this.#currentExplorer;
        let attemptNet =
            preferredNetwork?.isRpc === isRPC
                ? preferredNetwork
                : candidateNetworks[0];

        const nMaxTries = candidateNetworks.length;
        let i = candidateNetworks.findIndex((net) => attemptNet === net);
        if (i === -1) {
            debugWarn(
                DebugTopics.NET,
                `Cannot find index in ${isRPC ? 'RPC' : 'Explorer'} networks array`
            );
            i = 0;
            attemptNet = candidateNetworks[i];
        }

        // Run the call until successful, or all attempts exhausted
        for (let attempts = 1; attempts <= nMaxTries; attempts++) {
            try {
                debugLog(
                    DebugTopics.NET,
                    'attempting ' + funcName + ' on ' + attemptNet.strUrl
                );
                const res = await attemptNet[funcName](...args);
                return res;
            } catch (error) {
                debugLog(
                    DebugTopics.NET,
                    attemptNet.strUrl +
                        ' failed on ' +
                        funcName +
                        ' with error ' +
                        error
                );
                // Switch instances
                if (attempts === nMaxTries) {
                    throw error;
                }
                await sleep(retryTimeout);
                attemptNet = candidateNetworks[(i + attempts) % nMaxTries];
            }
        }
    }

    /**
     * Sometimes blockbook might return internal error, in this case this function will sleep for some times and retry
     * @param {string} funcName - The function to call
     * @param {boolean} isRPC - Whether to begin with the selected explorer or RPC node
     * @param  {...any} args - The arguments to pass to the function
     * @returns {Promise<Object>} Explorer result in json
     */
    async #safeFetch(funcName, isRPC, ...args) {
        let trials = 0;
        const sleepTime = 20000;
        const maxTrials = 6;
        while (trials < maxTrials) {
            trials += 1;
            try {
                return await this.#retryWrapper(funcName, isRPC, 0, ...args);
            } catch (e) {
                debugLog(
                    DebugTopics.NET,
                    'Blockbook internal error! sleeping for ' +
                        sleepTime +
                        ' seconds'
                );
                await sleep(sleepTime);
            }
        }
        throw new Error('Cannot safe fetch');
    }

    async getBlock(blockHeight) {
        return await this.#safeFetch('getBlock', true, blockHeight);
    }

    async getTxPage(nStartHeight, addr, n) {
        return await this.#safeFetch('getTxPage', false, nStartHeight, addr, n);
    }

    async getNumPages(nStartHeight, addr) {
        return await this.#safeFetch('getNumPages', false, nStartHeight, addr);
    }

    async getUTXOs(strAddress) {
        return await this.#retryWrapper('getUTXOs', false, 0, strAddress);
    }

    async getXPubInfo(strXPUB) {
        return await this.#retryWrapper('getXPubInfo', false, 0, strXPUB);
    }

    async getShieldBlockList() {
        return await this.#retryWrapper('getShieldBlockList', true, 0);
    }

    async getBlockCount() {
        try {
            return await this.#retryWrapper('getBlockCount', true, 0);
        } catch (rpcError) {
            debugWarn(
                DebugTopics.NET,
                `RPC getBlockCount failed, falling back to explorer: ${rpcError}`
            );
            return await this.#retryWrapper('getBlockCount', false, 0);
        }
    }

    async getBestBlockHash() {
        try {
            return await this.#retryWrapper('getBestBlockHash', true, 0);
        } catch (rpcError) {
            debugWarn(
                DebugTopics.NET,
                `RPC getBestBlockHash failed, falling back to explorer: ${rpcError}`
            );
            return await this.#retryWrapper('getBestBlockHash', false, 0);
        }
    }

    async sendTransaction(hex) {
        try {
            const data = await this.#retryWrapper(
                'sendTransaction',
                false,
                0,
                hex
            );

            // Throw and catch if the data is not a TXID
            if (!data.result || data.result.length !== 64) throw data;

            debugLog(DebugTopics.NET, 'Transaction sent! ' + data.result);
            getEventEmitter().emit('transaction-sent', true, data.result);
            return data.result;
        } catch (e) {
            getEventEmitter().emit('transaction-sent', false, e);
            return false;
        }
    }

    async getTxInfo(txHash) {
        return await this.#retryWrapper('getTxInfo', false, 0, txHash);
    }

    /**
     * @param{string} collateralTxId - masternode collateral transaction id
     * @param{number} outidx - masternode collateral output index
     */
    async getMasternodeInfo(collateralTxId, outidx) {
        return await this.#retryWrapper(
            'getMasternodeInfo',
            true,
            0,
            collateralTxId,
            outidx
        );
    }

    async getMasternodes() {
        return await this.#retryWrapper('getMasternodes', true, 0);
    }

    async getMasternodeCount() {
        return await this.#retryWrapper('getMasternodeCount', true, 0);
    }

    async getNextSuperblock() {
        return await this.#retryWrapper('getNextSuperblock', true, 0);
    }

    async startMasternode(broadcastMsg) {
        return await this.#retryWrapper(
            'startMasternode',
            true,
            0,
            broadcastMsg
        );
    }

    async getProposals() {
        return await this.#retryWrapper('getProposals', true, 0);
    }

    async getProposalHybridStatus(proposalHash) {
        return await this.#retryWrapper(
            'getProposalHybridStatus',
            true,
            0,
            proposalHash
        );
    }

    async listGovernanceVoteLocks(proposalHash = null) {
        return await this.#retryWrapper(
            'listGovernanceVoteLocks',
            true,
            0,
            proposalHash
        );
    }

    async createGovernanceVoteLock(proposalHash, amount, unlockHeight) {
        return await this.#retryWrapper(
            'createGovernanceVoteLock',
            true,
            0,
            proposalHash,
            amount,
            unlockHeight
        );
    }

    async castGovernanceVote(proposalHash, voteCode, lockRefs) {
        return await this.#retryWrapper(
            'castGovernanceVote',
            true,
            0,
            proposalHash,
            voteCode,
            lockRefs
        );
    }

    /**
     * Returns the proposal vote of a given masternode
     * @param {string} proposalName - name of the proposal
     * @param{string} collateralTxId - masternode collateral transaction id
     * @param{number} outidx - masternode collateral output index
     */
    async getProposalVote(proposalName, collateralTxId, outidx) {
        return await this.#retryWrapper(
            'getProposalVote',
            true,
            0,
            proposalName,
            collateralTxId,
            outidx
        );
    }

    /**
     * @param {string} collateralTxId - masternode collateral transaction id
     * @param {number} outidx - masternode collateral output index
     * @param {string} hash - the hash of the proposal to vote
     * @param {number} voteCode - the vote code. "Yes" is 1, "No" is 2
     * @param {number} sigTime - vote signature time
     * @param {string} signature - vote signature
     */
    async voteProposal(
        collateralTxId,
        outidx,
        hash,
        voteCode,
        sigTime,
        signature
    ) {
        return await this.#retryWrapper(
            'voteProposal',
            true,
            0,
            collateralTxId,
            outidx,
            hash,
            voteCode,
            sigTime,
            signature
        );
    }

    async getShieldData(initialBlock = 0) {
        return await this.#retryWrapper('getShieldData', true, 0, initialBlock);
    }

    async getShieldDataLength(startBlock, endBlock) {
        return await this.#retryWrapper(
            'getShieldDataLength',
            true,
            0,
            startBlock,
            endBlock
        );
    }

    async getSaplingOutput() {
        return await this.#retryWrapper('getSaplingOutput', true, 0);
    }

    async getSaplingSpend() {
        return await this.#retryWrapper('getSaplingSpend', true, 0);
    }

    /**
     * Submit a proposal
     * @param {Object} options
     * @param {String} options.name - Name of the proposal
     * @param {String} options.url - Url of the proposal
     * @param {Number} options.nPayments - Number of cycles this proposal is gonna last
     * @param {Number} options.start - Superblock of when the proposal is going to start
     * @param {String} options.address - Base58 encoded PIVX address
     * @param {Number} options.monthlyPayment - Payment amount per cycle in satoshi
     * @param {String} options.txid - Transaction id of the proposal fee
     */
    async submitProposal({
        name,
        url,
        nPayments,
        start,
        address,
        monthlyPayment,
        txid,
    }) {
        return await this.#retryWrapper('submitProposal', true, 2000, {
            name,
            url,
            nPayments,
            start,
            address,
            monthlyPayment,
            txid,
        });
    }

    static #instance = new NetworkManager();

    static getInstance() {
        return this.#instance;
    }
}

/**
 * Gets the network in use by MPW.
 * @returns {NetworkManager} Returns the network manager in use.
 */
export function getNetwork() {
    return NetworkManager.getInstance();
}
